/**
 * Endpoints sobre operaciones ya creadas: reintentar (envío al SNT cuando
 * estado=error) y editar (agregar/quitar series, también solo para estado=error).
 */

import { z } from 'zod';
import { OperacionSchema } from '@/api/contracts/operaciones';
import { getDb, nextMovId } from '@/api/mock/db';
import { logApi } from '@/stores/apilog';
import { ApiError } from './auth';

const MOCK_LATENCY_MS = 150;
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// REINTENTAR ENVÍO ANMAT (operación en estado 'error')
// =============================================================================

export const ReintentarAnmatRequest = z.object({
  operacionId: z.string().min(1),
  usuarioId: z.string().min(1),
});

export const ReintentarAnmatResponse = z.object({
  operacion: OperacionSchema,
});

export type ReintentarAnmatRequest = z.infer<typeof ReintentarAnmatRequest>;
export type ReintentarAnmatResponse = z.infer<typeof ReintentarAnmatResponse>;

export async function reintentarAnmat(
  input: ReintentarAnmatRequest,
): Promise<ReintentarAnmatResponse> {
  const req = ReintentarAnmatRequest.parse(input);
  await delay(MOCK_LATENCY_MS);
  const db = getDb();
  const op = db.operaciones.find((o) => o.id === req.operacionId);
  if (!op) throw new ApiError('Operación no encontrada', 'OP_NOT_FOUND');
  if (op.anmat.estado !== 'error') {
    throw new ApiError(
      `Solo se reintentan operaciones en estado 'error' (actual: ${op.anmat.estado})`,
      'OP_NOT_ERROR',
    );
  }

  const ahora = new Date().toISOString();
  op.anmat.estado = 'en_cola';
  op.anmat.intentos = (op.anmat.intentos ?? 0) + 1;
  op.anmat.ultimoError = undefined;
  op.anmat.mensajes.push({
    ts: ahora,
    tipo: 'request',
    body: `${op.anmat.tipoMensaje} reenviado (intento ${op.anmat.intentos})`,
  });

  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: `/snt/${op.anmat.tipoMensaje}`,
    status: 200,
    operacionId: op.id,
    requestBody: { reintento: op.anmat.intentos },
    responseBody: { estado: 'aceptado_en_cola' },
  });

  // Simular response del SNT (2s) — 80% éxito en reintento (un poco mejor que la primera)
  setTimeout(() => {
    if (op.anmat.estado === 'cancelada') return;
    const success = Math.random() > 0.2;
    if (success) {
      const codigoTx = 'TX-' + Math.random().toString(36).slice(2, 10).toUpperCase();
      op.anmat.estado = op.anmat.tipoMensaje === 'confirmarTransaccion' ? 'confirmada' : 'informada';
      op.anmat.codigoTransaccion = codigoTx;
      op.anmat.mensajes.push({ ts: new Date().toISOString(), tipo: 'response', body: `OK · ${codigoTx}` });
      for (const m of db.movimientos) {
        if (m.operacionId === op.id && !m.codigoTransaccion) m.codigoTransaccion = codigoTx;
      }
    } else {
      op.anmat.estado = 'error';
      op.anmat.ultimoError = {
        code: 'E099',
        msg: 'Error simulado del SNT (reintento)',
        severidad: 'medio',
        ts: new Date().toISOString(),
      };
      op.anmat.mensajes.push({
        ts: new Date().toISOString(),
        tipo: 'response',
        body: `Error E099 (reintento ${op.anmat.intentos})`,
      });
    }
  }, 2000);

  return ReintentarAnmatResponse.parse({ operacion: op });
}

// =============================================================================
// EDITAR OPERACIÓN CON ERROR (agregar/quitar series + razón obligatoria)
// =============================================================================

export const EditarOperacionRequest = z.object({
  operacionId: z.string().min(1),
  usuarioId: z.string().min(1),
  /** Lista nueva de series — el endpoint diff'ea contra la actual. */
  seriesIds: z.array(z.string()).min(1),
  razon: z.string().min(1, 'Razón obligatoria al editar una operación'),
});

export const EditarOperacionResponse = z.object({
  operacion: OperacionSchema,
  seriesAgregadas: z.number().int().nonnegative(),
  seriesQuitadas: z.number().int().nonnegative(),
});

export type EditarOperacionRequest = z.infer<typeof EditarOperacionRequest>;
export type EditarOperacionResponse = z.infer<typeof EditarOperacionResponse>;

export async function editarOperacion(
  input: EditarOperacionRequest,
): Promise<EditarOperacionResponse> {
  const req = EditarOperacionRequest.parse(input);
  await delay(MOCK_LATENCY_MS);
  const db = getDb();
  const op = db.operaciones.find((o) => o.id === req.operacionId);
  if (!op) throw new ApiError('Operación no encontrada', 'OP_NOT_FOUND');
  if (op.anmat.estado !== 'error') {
    throw new ApiError(
      `Solo se editan operaciones en estado 'error' (actual: ${op.anmat.estado})`,
      'OP_NOT_ERROR',
    );
  }

  const ahora = new Date().toISOString();
  const setActual = new Set(op.seriesIds);
  const setNuevo = new Set(req.seriesIds);
  const agregadas = req.seriesIds.filter((s) => !setActual.has(s));
  const quitadas = op.seriesIds.filter((s) => !setNuevo.has(s));

  // Validar agregadas: existen, están en mi GLN, en_poder
  for (const sid of agregadas) {
    const s = db.series.find((x) => x.id === sid);
    if (!s) throw new ApiError(`Serie ${sid} no encontrada`, 'SERIES_NOT_FOUND');
    if (s.ubicacionActualId !== op.ubicacionId)
      throw new ApiError(`Serie ${sid} no está en la ubicación origen`, 'SERIES_UBICACION');
    if (s.tenencia !== 'en_poder')
      throw new ApiError(`Serie ${sid} no está disponible (${s.tenencia})`, 'SERIES_UNAVAILABLE');
  }

  // Aplicar cambios
  for (const sid of agregadas) {
    const s = db.series.find((x) => x.id === sid);
    if (s) s.tenencia = 'en_transito';
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: op.id,
      tipoEvento: op.tipo === 'recepcion' ? 'recepcion' : op.tipo,
      fecha: ahora,
      ubicacionId: op.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: op.eventoAnmatId,
      detalle: `Editar operación · agregada · razón: ${req.razon}`,
    });
  }
  for (const sid of quitadas) {
    const s = db.series.find((x) => x.id === sid);
    if (s && s.tenencia === 'en_transito') {
      s.tenencia = 'en_poder';
    }
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: op.id,
      tipoEvento: 'reversion_error',
      fecha: ahora,
      ubicacionId: op.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: null,
      detalle: `Editar operación · quitada · razón: ${req.razon}`,
    });
  }

  op.seriesIds = [...req.seriesIds];
  op.anmat.estado = 'en_cola';
  op.anmat.intentos = (op.anmat.intentos ?? 0) + 1;
  op.anmat.ultimoError = undefined;
  op.anmat.mensajes.push({
    ts: ahora,
    tipo: 'request',
    body: `Edición · +${agregadas.length} / -${quitadas.length} · razón: ${req.razon}`,
  });

  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: `/snt/${op.anmat.tipoMensaje}`,
    status: 200,
    operacionId: op.id,
    requestBody: {
      reintento: op.anmat.intentos,
      modo: 'edicion',
      agregadas: agregadas.length,
      quitadas: quitadas.length,
      motivo: req.razon,
    },
    responseBody: { estado: 'aceptado_en_cola' },
  });

  // Schedule respuesta SNT (igual que reintentar)
  setTimeout(() => {
    if (op.anmat.estado === 'cancelada') return;
    const success = Math.random() > 0.15;
    if (success) {
      const codigoTx = 'TX-' + Math.random().toString(36).slice(2, 10).toUpperCase();
      op.anmat.estado = op.anmat.tipoMensaje === 'confirmarTransaccion' ? 'confirmada' : 'informada';
      op.anmat.codigoTransaccion = codigoTx;
      op.anmat.mensajes.push({ ts: new Date().toISOString(), tipo: 'response', body: `OK · ${codigoTx}` });
      for (const m of db.movimientos) {
        if (m.operacionId === op.id && !m.codigoTransaccion) m.codigoTransaccion = codigoTx;
      }
    } else {
      op.anmat.estado = 'error';
      op.anmat.ultimoError = {
        code: 'E099',
        msg: 'Error simulado del SNT (post-edición)',
        severidad: 'medio',
        ts: new Date().toISOString(),
      };
      op.anmat.mensajes.push({ ts: new Date().toISOString(), tipo: 'response', body: 'Error E099 (post-edición)' });
    }
  }, 2000);

  return EditarOperacionResponse.parse({
    operacion: op,
    seriesAgregadas: agregadas.length,
    seriesQuitadas: quitadas.length,
  });
}
